import React, { useEffect } from "react";

export type NodeType = {
  id: string;
  name: string;
  children?: NodeType[];
  fk_videoModule_id?: string;
};

type TreeNodeProps = {
  node: NodeType;
  checked: Record<string, boolean>;
  onCheck: (id: string, isChecked: boolean) => void;
  parentChecked?: boolean;
};

export const TreeNode: React.FC<TreeNodeProps> = ({ 
  node, 
  checked, 
  onCheck
}) => {
  const isChecked = checked[node.id] || false;
  
  // Handle checkbox change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    
    // Update only this node
    onCheck(node.id, isChecked);
    
    // Note: We're not propagating selection to children anymore
  };
  
  // Check if any children are checked (for determining parent selection)
  const anyChildrenChecked = node.children && node.children.length > 0 && 
    node.children.some(child => {
      if (checked[child.id]) return true;
      
      if (child.children && child.children.length > 0) {
        return areAnyDescendantsChecked(child);
      }
      
      return false;
    });
  
  // Helper function to check if any descendants of a node are checked
  const areAnyDescendantsChecked = (node: NodeType): boolean => {
    if (!node.children || node.children.length === 0) return !!checked[node.id];
    
    return node.children.some(child => {
      if (checked[child.id]) return true;
      
      if (child.children && child.children.length > 0) {
        return areAnyDescendantsChecked(child);
      }
      
      return false;
    });
  };
  
  useEffect(() => {
    // If any child is checked and this node isn't, check this node
    if (anyChildrenChecked && !checked[node.id]) {
      onCheck(node.id, true);
    }
  }, [checked, node.id, onCheck, anyChildrenChecked]);
  
  return (
    <div className="ml-4 mt-2">
      <div className="flex items-center">
        <input
          type="checkbox"
          id={node.id}
          checked={isChecked}
          onChange={handleChange}
          className="mr-2"
        />
        <label htmlFor={node.id} className="select-none">
          {node.name}
        </label>
      </div>
      
      {node.children && node.children.length > 0 && (
        <div className="ml-4">
          {node.children.map(childNode => (
            <TreeNode
              key={childNode.id}
              node={childNode}
              checked={checked}
              onCheck={onCheck}
            />
          ))}
        </div>
      )}
    </div>
  );
};